import type { Data, InitOptions } from '@converge/cookie-management';
// 只读子路径不带 Cookie Manager 的 UI 与单例，可以同步静态引入；包根仍按需动态加载。
import { readPersistedConsentData } from '@converge/cookie-management/persisted-consent';
import { updateGtmConsent } from '@/contexts/consent/google-consent';

type CookieManager = (typeof import('@converge/cookie-management'))['cookieManager'];

export interface ConsentSnapshot {
    readonly data: Data | null;
    readonly resolution: 'pending' | 'resolved' | 'error';
    readonly isReady: boolean;
    readonly cookieAvailable: boolean;
    readonly functionalAllowed: boolean;
    readonly analyticsAllowed: boolean;
    readonly advertisingAllowed: boolean;
}

const INITIAL_SNAPSHOT: ConsentSnapshot = Object.freeze({
    data: null,
    resolution: 'pending',
    isReady: false,
    cookieAvailable: true,
    functionalAllowed: false,
    analyticsAllowed: false,
    advertisingAllowed: false,
});

let snapshot = INITIAL_SNAPSHOT;
let manager: CookieManager | null = null;
let initialization: Promise<void> | null = null;
let initialized = false;
let latestOptions: InitOptions = { locale: 'en', theme: 'light' };
let unsubscribeManager: (() => void) | null = null;
const listeners = new Set<() => void>();

function cookiesAvailable(): boolean {
    return typeof navigator === 'undefined' || navigator.cookieEnabled !== false;
}

function publish(data: Data | null, resolution: ConsentSnapshot['resolution']): void {
    const available = cookiesAvailable();
    const resolvedData = resolution === 'resolved' && available ? data : null;
    const next = {
        data: resolvedData,
        resolution,
        isReady: resolution === 'resolved',
        cookieAvailable: available,
        functionalAllowed: resolvedData?.preference?.functional === true,
        analyticsAllowed: resolvedData?.preference?.analytics === true,
        advertisingAllowed: resolvedData?.preference?.advertising === true,
    };
    if (Object.entries(next).every(([key, value]) => key === 'data'
        ? JSON.stringify(snapshot.data) === JSON.stringify(value)
        : snapshot[key as keyof ConsentSnapshot] === value)) return;
    snapshot = Object.freeze(next);
    // 先让 SDK 同步收紧门禁，再向 Google 映射新的授权值。
    for (const listener of listeners) {
        try { listener(); }
        catch { console.error('[consent] subscriber failed'); }
    }
    try { updateGtmConsent(resolvedData); }
    catch { console.error('[consent] Google consent sync failed'); }
}

/** 只更新 Cookie UI，不能覆盖偏好或再次初始化 SDK。 */
export function updateConsentPresentation(options: InitOptions): void {
    latestOptions = { locale: options.locale ?? 'en', theme: options.theme ?? 'light' };
    try { manager?.update(latestOptions); }
    catch { console.error('[consent] presentation update failed'); }
}

function subscribeManager(): void {
    if (!manager || unsubscribeManager || listeners.size === 0) return;
    unsubscribeManager = manager.onDataChange((data) => publish(data, 'resolved'));
}

let restoredFromCookie = false;

/**
 * 回访用户的明确选择已经在 Cookie 里，结论与 Cookie Manager 初始化后读到的一致，
 * 不必等它加载完成；没有选择时保持未决，不能当成拒绝。只在 SDK 接管前生效一次。
 */
function restorePersistedConsent(): void {
    if (restoredFromCookie || initialized || typeof window === 'undefined') return;
    restoredFromCookie = true;
    if (!cookiesAvailable()) return;
    const persisted = readPersistedConsentData();
    if (persisted) publish(persisted, 'resolved');
}

export function getConsentSnapshot(): ConsentSnapshot {
    if (typeof window === 'undefined') return INITIAL_SNAPSHOT;
    restorePersistedConsent();
    return snapshot;
}

export function getServerConsentSnapshot(): ConsentSnapshot {
    return INITIAL_SNAPSHOT;
}

export function subscribeConsent(listener: () => void): () => void {
    restorePersistedConsent();
    listeners.add(listener);
    subscribeManager();
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
            unsubscribeManager?.();
            unsubscribeManager = null;
        }
    };
}

/** 参考 Web 的异步单次加载；错误可重试，语言更新不被初始化 Promise 吞掉。 */
export function initializeConsentStore(options: InitOptions): Promise<void> {
    if (typeof window === 'undefined') return Promise.resolve();
    latestOptions = { locale: options.locale ?? 'en', theme: options.theme ?? 'light' };

    if (!cookiesAvailable()) {
        publish(null, 'error');
        return Promise.resolve();
    }

    try {
        if (manager) {
            manager.update(latestOptions);
            subscribeManager();
        }
        if (initialization) return initialization;
        if (initialized && manager) {
            publish(manager.getData(), 'resolved');
            return Promise.resolve();
        }
    } catch {
        publish(null, 'error');
        return Promise.resolve();
    }

    const request = import('@converge/cookie-management')
        .then(async (module) => {
            manager = module.cookieManager;
            subscribeManager();
            manager.update(latestOptions);
            await manager.init(latestOptions);
            // 初始化期间可能切换过语言；只应用最后一次输入。
            manager.update(latestOptions);
            initialized = true;
            publish(manager.getData(), 'resolved');
        })
        .catch(() => {
            publish(null, 'error');
            console.error('[consent] initialization failed');
        })
        .finally(() => {
            if (initialization === request) initialization = null;
        });
    initialization = request;
    return request;
}

export function openPreferenceCenter(): void {
    void initializeConsentStore(latestOptions)
        .then(() => manager?.openPreferenceCenter())
        .catch(() => console.error('[consent] preference center unavailable'));
}

export function resetConsentStoreForTests(): void {
    unsubscribeManager?.();
    unsubscribeManager = null;
    manager = null;
    initialization = null;
    initialized = false;
    restoredFromCookie = false;
    snapshot = INITIAL_SNAPSHOT;
    latestOptions = { locale: 'en', theme: 'light' };
    listeners.clear();
}
